import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { feesApi } from "../api/feesApi";

type StudentRow = {
  studentId: string;
  firstName?: string;
  lastName?: string;
  scholarNumber?: string;
  class?: { id: string; name: string };
  section?: { id: string; name: string };
};

const amount = (v: any) => Number(v || 0);

const NoDuesCertificatePage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchMasters, fetchSections } = useMasterData();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [search, setSearch] = useState("");
  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);

  const [students, setStudents] = useState<StudentRow[]>([]);
  const [studentId, setStudentId] = useState("");

  const [summary, setSummary] = useState<any>(null);
  const [eligibility, setEligibility] = useState<any>(null);

  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchAcademicSessions();
    fetchMasters();
  }, [fetchAcademicSessions, fetchMasters]);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (!sessionId && active) {
      setSessionId(active.id);
    }
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const rows = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(rows) ? rows : []);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  const loadStudents = async () => {
    if (!sessionId) {
      setError("Academic Session is required");
      return;
    }
    setLoadingStudents(true);
    setError(null);
    setMessage(null);
    try {
      const rows = await feesApi.students({
        sessionId,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        search: search || undefined,
        limit: 100,
      });
      setStudents(Array.isArray(rows) ? rows : []);
      if (!rows?.length) setMessage("No students found for selected filters");
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to load students");
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadStatus = async (selectedStudentId: string) => {
    if (!selectedStudentId || !sessionId) return;
    setLoadingStatus(true);
    setError(null);
    setMessage(null);
    try {
      const [summaryData, checkData] = await Promise.all([
        feesApi.getSummary(selectedStudentId, sessionId),
        feesApi.noDuesCheck(selectedStudentId, sessionId),
      ]);
      setSummary(summaryData);
      setEligibility(checkData);
    } catch (e: any) {
      setSummary(null);
      setEligibility(null);
      setError(e?.response?.data?.message || e?.message || "Failed to check no dues eligibility");
    } finally {
      setLoadingStatus(false);
    }
  };

  const selectedStudent = useMemo(
    () => students.find((s) => s.studentId === studentId) || null,
    [students, studentId],
  );

  const selectedStudentClassName = useMemo(() => {
    const classIdFromStudent = selectedStudent?.class?.id;
    if (!classIdFromStudent) return selectedStudent?.class?.name || "-";
    const classFromMasters = masters.classes.find((c) => c.id === classIdFromStudent);
    return classFromMasters?.name || selectedStudent?.class?.name || "-";
  }, [masters.classes, selectedStudent]);

  const canGenerate = Boolean(
    eligibility?.eligible
    && amount(summary?.finalDue) === 0,
  );

  const generateCertificate = async () => {
    if (!studentId || !sessionId) {
      setError("Student and Session are required");
      return;
    }
    if (!canGenerate) {
      setError(eligibility?.reason || "Student is not eligible for No Dues Certificate");
      return;
    }

    setGenerating(true);
    setError(null);
    setMessage(null);
    try {
      const file = await feesApi.generateNoDuesCertificateBlob({ studentId, sessionId });
      const url = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setMessage("No Dues Certificate generated successfully");
      await loadStatus(studentId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to generate No Dues Certificate");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          No Dues Certificate
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Filter Panel
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Academic Session"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Class"
                value={classId}
                onChange={(e) => setClassId(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Section"
                value={sectionId}
                disabled={!classId}
                onChange={(e) => setSectionId(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Student Search (name / scholar no)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth variant="contained" onClick={loadStudents} disabled={loadingStudents}>
                {loadingStudents ? "Loading..." : "Search Students"}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Student Select
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                select
                fullWidth
                label="Student"
                value={studentId}
                onChange={(e) => {
                  const id = e.target.value;
                  setStudentId(id);
                  setSummary(null);
                  setEligibility(null);
                  if (id) {
                    loadStatus(id);
                  }
                }}
              >
                {students.map((s) => (
                  <MenuItem key={s.studentId} value={s.studentId}>
                    {`${s.firstName || ""} ${s.lastName || ""}`.trim()} ({s.scholarNumber || "-"})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Class - Section"
                value={selectedStudent ? `${selectedStudentClassName} - ${selectedStudent?.section?.name || "-"}` : ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Fee Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}><Typography>Total Fee: {amount(summary?.totalFee).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography>Paid: {amount(summary?.totalPaid).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography>Discount: {amount(summary?.totalDiscount).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography>Refund: {amount(summary?.totalRefund).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography>Penalty: {amount(summary?.totalPenalty).toFixed(2)}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography sx={{ fontWeight: 700 }}>Final Due: {amount(summary?.finalDue).toFixed(2)}</Typography></Grid>
          </Grid>

          {loadingStatus && <Typography sx={{ mt: 1 }}>Checking eligibility...</Typography>}

          {eligibility && (
            <Alert sx={{ mt: 2 }} severity={eligibility.eligible ? "success" : "warning"}>
              {eligibility.eligible
                ? "Eligible for No Dues Certificate"
                : eligibility.reason || "Not eligible for No Dues Certificate"}
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={generateCertificate}
              disabled={!canGenerate || generating || !studentId || !sessionId}
            >
              {generating ? "Generating..." : "Generate Certificate"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default NoDuesCertificatePage;
